import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AnalyticsTrackingType, TrackErrorData } from '@rogers/self-serve-analytics';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { PasswordHint } from '@rogers/cdk/form';
import { CookieService } from 'ngx-cookie-service';
import { ConfigurationService } from 'src/app/shared/configuration.service';
import { GlobalStateService } from 'src/app/shared/global-state.service';

@Component({
    selector: 'app-confirm-password',
    templateUrl: './confirm-password.component.html',
    styleUrls: ['./confirm-password.component.scss'],
    standalone: false
})
export class ConfirmPasswordComponent implements OnInit, OnDestroy, AfterViewInit {
  private setPasswordValueChanges;
  private newPasswordValue = '';
  private confirmPasswordValue = '';
  setPasswordForm: UntypedFormGroup;
  hideNewPassword = true;
  hideConfirmPassword = true;
  hidePasswordTip = true;
  hideConfirmPasswordTip = true;
  username: string;
  @ViewChild('setNewPassswordRef')
  setNewPassswordRef: ElementRef;
  @ViewChild('newPasswordRef')
  newPassswordRef: ElementRef;
  passwordHintsCaps = false;
  blankPasswordSubmit = false;
  isNewPassordValid = true;
  client: string | null;
  passwordExpiryError;
  passwordExpiryErrorMessage;
  showRemindMeButton: boolean = false;
  errorVariant;
  errorIcon;
  showError: boolean = false;

  onResetPasswordSubmit: (code: string) => void;
  onChangePasswordSkipClick: () => void;

  newPasswordHints: Array<PasswordHint> = [
    {
      type: 'message',
      message: 'rogers.setPassword.hint.minimumEight'
    },
    {
      type: 'message',
      message: 'rogers.setPassword.hint.oneUpperCase'
    },
    {
      type: 'message',
      message: 'rogers.setPassword.hint.oneLowerCase'
    },
    {
      type: 'message',
      message: 'rogers.setPassword.hint.oneNumber'
    },
    {
      type: 'message',
      message: 'rogers.setPassword.hint.oneSpecialChar'
    },
    {
      type: 'message',
      message: 'rogers.setPassword.hint.allowedCharacters',
      isVisible: false
    },
    {
      type: 'fnKey',
      message: 'rogers.setPassword.hint.capsLockOn',
      icon: 'capslock',
      isVisible: false
    }
  ];
  confirmHints: Array<PasswordHint> = [
    {
      type: 'message',
      message: 'rogers.setPassword.message.passwordMatch'
    },
    {
      type: 'fnKey',
      message: 'rogers.setPassword.hint.capsLockOn',
      icon: 'capslock',
      isVisible: false
    }
  ];

  callInProgress = false;
  apiErrorTranslationKey: string;
  passwordValidationPattern: RegExp;
  passwordValidationPatternWithoutLength: RegExp;
  userName = '';

  track: Record<string, TrackErrorData> = {
    changePasswordConfirmPageview: {
      pageView: true,
      isModalPopup: false,
      mappingKey: 'change-password-confirm',
    },
    changePasswordConfirmErrors: {
      pageView: false,
      isModalPopup: false,
      mappingKey: 'change-password-confirm',
      category: 'self serve',
      type: '' as AnalyticsTrackingType,
      code: '',
      API: this.configurationService.config.analyticsAPIUrl,
      field: ''
    },
    changePasswordConfirmFormError: {
      pageView: false,
      isModalPopup: false,
      mappingKey: 'change-password-confirm',
      category: 'self serve',
      type: 'form',
      code: '',
      API: '',
      field: 'password'
    },
  };

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly configurationService: ConfigurationService,
    private readonly globalStateService: GlobalStateService,
    private readonly cookieService: CookieService,
    private readonly titleService: Title,
    private readonly translate: TranslateService
  ) {
    this.passwordValidationPattern =
    configurationService.config.passwordValidationPattern;
    this.passwordValidationPatternWithoutLength =
    configurationService.config.passwordValidationPatternWithoutLength;
  }
  ngOnInit() {
    this.client  = this.cookieService.get('client');
    this.userName = this.cookieService.get('username');
    this.globalStateService.setHideLangToggle(false);
    this.createForm();

    this.passwordExpiryError = this.cookieService.get('passwordExpiry');

    if(this.passwordExpiryError === 'UE23'){
      this.passwordExpiryErrorMessage = 'rogers.setPassword.message.passwordHistoryError';
      this.errorIcon = 'error-filled';
      this.errorVariant = 'error';
      this.showRemindMeButton = false;
    }
    
    if (this.client === 'shaw' || this.client === 'shaw_direct' || this.client === 'shaw_enterprise'){
      setTimeout(() => {
        this.titleService.setTitle(
          this.translate.instant(this.client + '.login.title')
        );
      }, 500);
    }
  }

  ngAfterViewInit() {
     setTimeout(_ => {
      this.newPassswordRef.nativeElement.focus();
    }, 5);
  }

   // Create a new form for Forgot Username
  createForm() {
    this.setPasswordForm = this.formBuilder.group(
      {
        userName: new UntypedFormControl(this.username),
        newPassword: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(this.passwordValidationPattern)
        ]),
        confirmPassword: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(this.passwordValidationPattern)
        ])
      },
      {
        validator: this.validateSetPasswordForm.bind(this)
      }
    );
    this.setPasswordValueChanges = this.setPasswordForm.valueChanges.subscribe(
      formControlList => this.setPasswordFormOnChange(formControlList)
    );
  }

  ngOnDestroy() {
    this.setPasswordValueChanges.unsubscribe();
  }

  setPasswordFormOnChange(ctrls) {
    if (!ctrls.hasOwnProperty('newPassword')) {
      return;
    }
    this.newPasswordValue = ctrls.newPassword;
    this.confirmPasswordValue = ctrls.confirmPassword;

    this.passwordChange(null);
  }

  passwordChange(key) {
    if (this.newPasswordValue.match(/[^0-9A-Za-z!@#$]/g)) {
      this.newPasswordHints[5].isVisible = true;
      this.newPasswordHints[5].valid = false;
    } else {
      this.newPasswordHints[5].isVisible = false;
    }

    this.newPasswordHints[1].valid = this.newPasswordValue.match(/[A-Z]/)? true : key;
    this.newPasswordHints[2].valid = this.newPasswordValue.match(/[a-z]/)? true : key;
    this.newPasswordHints[3].valid = this.newPasswordValue.match(/[0-9]/)? true : key;
    this.newPasswordHints[0].valid = this.newPasswordValue.length >= 8 ? true : key;

    this.newPasswordHints[4].valid = this.hasSpecialChar(this.newPasswordValue) ? true : key;

    if (
      this.confirmPasswordValue === this.newPasswordValue &&
      this.confirmPasswordValue.length >= 1 && this.newPasswordHints[0].valid
      && this.newPasswordHints[1].valid && this.newPasswordHints[2].valid
      && this.newPasswordHints[3].valid && this.newPasswordHints[4].valid
    ) {
      this.confirmHints[0].valid = true;
      this.setPasswordForm.controls.confirmPassword.setErrors(null);
    } else {
      this.confirmHints[0].valid = false;
      this.setPasswordForm.controls.confirmPassword.setErrors({
        valid: false
      });
    }

    const pwd = this.setPasswordForm.controls.newPassword.value;
    const confirmPwd = this.setPasswordForm.controls.confirmPassword.value;

    if (pwd === '' && confirmPwd === ''){
      this.isNewPassordValid = true;
    }
   else if (!this.newPasswordHints[0].valid || !this.newPasswordHints[1].valid
      || !this.newPasswordHints[2].valid || !this.newPasswordHints[3].valid){
        this.isNewPassordValid = false;
    }
    else{
      this.isNewPassordValid = true;
    }
  }

  validateSetPasswordForm(formGroup: UntypedFormGroup) {
    let validationFlag = false;
    if (
      formGroup &&
      formGroup.contains('newPassword') &&
      formGroup.contains('confirmPassword')
    ) {
      if (
        formGroup.controls.newPassword.value ===
        formGroup.controls.confirmPassword.value
      ) {
        validationFlag = true;
      }
    }
    return validationFlag
      ? null
      : {
          validateForm: {
            valid: false
          }
        };
  }

  onSetPasswordSubmit(){

    if (this.setPasswordForm.invalid)
    {
      if (this.hideConfirmPasswordTip) { this.hideConfirmPasswordTip = false; }
      this.passwordChange(false);
      const errorField = this.setNewPassswordRef.nativeElement.querySelectorAll(
        'ds-form-field.-error input'
      );

      if ((this.newPasswordValue === '' || this.newPasswordValue === undefined )
      && (this.confirmPasswordValue === '' ||  this.confirmPasswordValue === undefined)){
        this.blankPasswordSubmit = true;
      }
      return;
    }

    this.callInProgress = true;
    this.onResetPasswordSubmit(this.newPasswordValue);
  }

  checkOnKeyChange(event: KeyboardEvent, type: string): void {
    if ((this.newPasswordValue === '' || this.newPasswordValue === undefined )
    && (this.confirmPasswordValue === '' ||  this.confirmPasswordValue === undefined)){
      this.blankPasswordSubmit = true;
    }
    else{
      this.blankPasswordSubmit = false;
    }

    this.passwordHintsCaps =
      event.getModifierState && event.getModifierState('CapsLock');
    if (type === 'new') {
        this.newPasswordHints[5].isVisible = this.passwordHintsCaps;
      }
    if (type === 'confirm') {
        this.confirmHints[1].isVisible = this.passwordHintsCaps;
      }
  }

  onChangePasswordCancel(event: Event): void {
    event.preventDefault();
    this.onChangePasswordSkipClick();
   }

  onPasswordBlur(event: FocusEvent){
    this.newPasswordHints[0].valid = this.newPasswordValue.length >= 8 ? true : false;
    this.newPasswordHints[1].valid = this.newPasswordValue.match(/[A-Z]/)? true : false;
    this.newPasswordHints[2].valid = this.newPasswordValue.match(/[a-z]/)? true : false;
    this.newPasswordHints[3].valid = this.newPasswordValue.match(/[0-9]/)? true : false;
    this.newPasswordHints[4].valid = this.hasSpecialChar(this.newPasswordValue) ? true : false;

    if(!this.newPasswordHints[4].valid || this.newPasswordHints[4].valid === null){
      this.showError = true;
    }
    else{
       this.showError = false;
    }
  }

  hasSpecialChar(input: string): boolean {
  const regex = /[!@#$]/;
  return regex.test(input);
  }
}
